/**
 * O pedido por trás do cartão de ajuste (D1/D11). O evento `message.created`
 * (o worker avisa a conversa em cada marco) invalida esta query em
 * `useChatRealtime`; a repergunta de 10 s é a via que não depende de o evento
 * chegar — e para quando o pedido espera a pessoa ou terminou.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getDiffDoPedido, getPainelDoStage, getPedidoDoRascunho, getPedidoTerraform, retomarPedido,
  type PainelDoStage, type PedidoDoCartao,
} from '@/api/terraformRunner'

export const CHAVE_PEDIDO_TERRAFORM = 'terraform-change-request'
const REPERGUNTA_MS = 10000

export function intervaloDeRepergunta(pedido: PedidoDoCartao | undefined): number | false {
  if (!pedido) return REPERGUNTA_MS
  // Esperando a pessoa (o Confirmar, ou o "Quero seguir" do guardado): o
  // `message.created` do próximo marco invalida a query.
  if (pedido.terminal || pedido.estado === 'aguardando_confirmacao'
      || pedido.estado === 'aguardando_pedinte') return false
  return REPERGUNTA_MS
}

/** O "Quero seguir" (spec 2026-10-15 D3). Em qualquer desfecho relê o pedido:
 * o 409 de um cartão velho também volta com o estado atual. */
export function useRetomarPedido() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pedidoId: number) => retomarPedido(pedidoId),
    onSettled: (_data, _erro, pedidoId) =>
      qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM, pedidoId] }),
  })
}

export function useTerraformChangeRequest(pedidoId: number | null | undefined) {
  return useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, pedidoId],
    queryFn: () => getPedidoTerraform(pedidoId as number),
    enabled: typeof pedidoId === 'number',
    refetchInterval: (query) => intervaloDeRepergunta(query.state.data),
  })
}

/** O pedido de criação por trás do cartão de `terraform_stage_create`, achado
 * pelo rascunho. Mesma chave-raiz: o `message.created` de cada marco invalida
 * este também. `null` desliga (proposta ainda não confirmada). */
export function usePedidoDoRascunho(rascunhoId: number | null | undefined) {
  return useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, 'rascunho', rascunhoId],
    queryFn: () => getPedidoDoRascunho(rascunhoId as number),
    enabled: typeof rascunhoId === 'number',
    // 404 = cartão de antes do motor, ou sem acesso: não há o que reperguntar.
    // Sem isto, todo cartão antigo da conversa perguntaria a cada 10 s.
    refetchInterval: (query) => (query.state.status === 'error'
      ? false : intervaloDeRepergunta(query.state.data)),
    retry: false,
  })
}

/** Há pedido vivo no stage: a âncora não terminou, alguém ocupa o stage (o
 * ocupante opaco também — ele só some quando termina) ou há fila. */
export function painelTemPedidoVivo(painel: PainelDoStage | undefined): boolean {
  if (!painel || !painel.ancora) return false
  if (!painel.ancora.terminal) return true
  if (painel.em_curso && (!painel.em_curso.visivel || !painel.em_curso.terminal)) return true
  return (painel.fila?.length ?? 0) > 0
}

/** O painel do @DevOps (spec 2026-11-04 §3.2): o stage do pedido âncora. Relê
 * a cada 10 s enquanto houver pedido vivo, como o painel do chamado
 * (`TerraformPedidoPanel`). Mesma chave-raiz do cartão: o `message.created`
 * invalida este também. `null` desliga (painel fechado). */
export function usePainelDoStage(pedidoId: number | null | undefined, viaChamado?: number | null) {
  return useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, 'painel-do-stage', pedidoId, viaChamado ?? null],
    queryFn: () => getPainelDoStage(pedidoId as number, viaChamado),
    enabled: typeof pedidoId === 'number',
    // 404 uniforme: quem vê não lê a âncora — não há o que reperguntar.
    retry: false,
    refetchInterval: (query) => (painelTemPedidoVivo(query.state.data) ? REPERGUNTA_MS : false),
  })
}

/** O diff do ajuste livre (spec 2026-11-04 §3.3), lido só quando a seção do painel abre
 *  (`enabled`). Sem repergunta: o patch do pedido não muda depois de gravado. */
export function useDiffDoPedido(pedidoId: number, viaChamado: number | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, 'diff', pedidoId, viaChamado ?? null],
    queryFn: () => getDiffDoPedido(pedidoId, viaChamado),
    enabled,
    retry: false,
    staleTime: Infinity,
  })
}
